import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const { FiUserCheck, FiAlertCircle, FiLoader, FiCheckCircle } = FiIcons;

function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, signIn, signUp } = useAuth();
  const { acceptInvitation } = useGuardian();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [authMode, setAuthMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    loadInvitation();
  }, [token]);

  useEffect(() => {
    if (user && invitation) {
      handleAcceptInvitation();
    }
  }, [user, invitation]);

  const loadInvitation = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('guardian_invitations')
        .select(`
          *,
          athlete:profiles!guardian_invitations_athlete_id_fkey(
            first_name,
            last_name,
            sport,
            graduation_year
          )
        `)
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .single();

      if (error || !data) {
        setError('Invalid or expired invitation link');
        return;
      }

      // Check if expired
      if (new Date(data.expires_at) < new Date()) {
        setError('This invitation has expired');
        return;
      }

      setInvitation(data);
      setEmail(data.guardian_email);
      setName(data.guardian_name);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Failed to load invitation details');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async () => {
    try {
      setAccepting(true);
      await acceptInvitation(token, user.id);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (authMode === 'signin') {
        const { error } = await signIn(email, password);
        if (error) throw error;
      } else {
        const { error } = await signUp(email, password);
        if (error) throw error;
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-space-grotesk font-bold text-secondary mb-2">
            Invalid Invitation
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary"
          >
            Go to Home
          </button>
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCheckCircle} className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-space-grotesk font-bold text-secondary mb-2">
            Access Granted!
          </h2>
          <p className="text-gray-600">
            You now have guardian access to manage this athlete's account.
          </p>
        </motion.div>
      </div>
    );
  }

  if (accepting) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Accepting invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiUserCheck} className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-space-grotesk font-bold text-secondary mb-2">
            Guardian Invitation
          </h2>
          <p className="text-gray-600">
            You've been invited to be a guardian for
          </p>
          <p className="text-lg font-semibold text-primary mt-2">
            {invitation?.athlete?.first_name} {invitation?.athlete?.last_name}
          </p>
          {invitation?.athlete?.sport && (
            <p className="text-sm text-gray-500">
              {invitation.athlete.sport}
              {invitation.athlete.graduation_year &&
                ` • Class of ${invitation.athlete.graduation_year}`}
            </p>
          )}
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded mb-6">
          <h4 className="font-semibold text-gray-800 mb-1">Guardian Access Includes:</h4>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>Full profile management</li>
            <li>School list and communications</li>
            <li>Timeline and deadline tracking</li>
            <li>Video and content management</li>
            <li>Academic records access</li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name
            </label>
            <input
              type="text"
              value={name}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              readOnly
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter your password"
              required
              minLength={6}
            />
          </div>

          <button
            type="submit"
            className="w-full bg-primary text-white py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary font-semibold"
          >
            {authMode === 'signin' ? 'Sign In & Accept' : 'Create Account & Accept'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setAuthMode(authMode === 'signin' ? 'signup' : 'signin')}
              className="text-sm text-primary hover:underline"
            >
              {authMode === 'signin'
                ? "Don't have an account? Sign up"
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default AcceptInvitation;